import { useState, useEffect, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Bot, LogOut, User, Users, Settings, Wand2, Activity, Bell, FlaskConical, Gamepad2,
  Workflow, Calendar, ChevronLeft, ChevronRight, ChevronDown, BarChart3, History,
  Tags, TrendingUp, MessageSquare, Flag, CreditCard, Package, PieChart, MousePointer,
  UserCog, Cog, Boxes, Wrench, FileCode, Palette, Scan, FileText,
  MessageSquareText, Image, Timer, Sparkles, DollarSign, Gift, Megaphone,
  Ticket, KeyRound, Wallet, AppWindowMac, ClipboardList

} from 'lucide-react'
import clsx from 'clsx'
import { supabase } from '@/lib/supabase'
import { useI18n } from '@/i18n'
import type { NavigationMessages } from '@/i18n/messages'
import { reportPageItems, type ReportPageId } from '@/features/reports/reportNavigation'

interface NavItem {
  nameKey: keyof NavigationMessages
  path: string
  icon: typeof Bot
}

interface NavGroup {
  key: string
  nameKey: keyof NavigationMessages
  icon: typeof Bot
  items: NavItem[]
}

const reportNavIconById: Record<ReportPageId, typeof Bot> = {
  overview: BarChart3,
  userCost: Users,
  pricing: DollarSign,
  trialRisk: Activity,
  behavior: MousePointer,
  revenue: TrendingUp,
}

function isNavItemActive(pathname: string, item: NavItem): boolean {
  if (item.path === '/') {
    return pathname === '/' || pathname.startsWith('/agents')
  }

  return pathname === item.path || pathname.startsWith(`${item.path}/`)
}

const navGroups: NavGroup[] = [
  {
    key: 'agent',
    nameKey: 'navGroupAgent',
    icon: Package,
    items: [
      { nameKey: 'agentConfigs', path: '/', icon: Bot },
      { nameKey: 'toolManagement', path: '/tool-management', icon: Wrench },
      { nameKey: 'businessTemplates', path: '/business-templates', icon: FileCode },
      { nameKey: 'skills', path: '/skills', icon: Wand2 },
      { nameKey: 'imageWorkflows', path: '/image-workflows', icon: Workflow },
      { nameKey: 'workflowSchedules', path: '/schedules', icon: Calendar },
    ]
  },
  {
    key: 'content',
    nameKey: 'navGroupContent',
    icon: Palette,
    items: [
      { nameKey: 'contentPacks', path: '/content-packs', icon: Package },
      { nameKey: 'appStoreReleases', path: '/app-store-releases', icon: AppWindowMac },
      { nameKey: 'welcomeTitles', path: '/welcome-titles', icon: MessageSquareText },
      { nameKey: 'petTips', path: '/pet-tips', icon: MessageSquare },
      { nameKey: 'uiAssets', path: '/ui-assets', icon: Image },
    ]
  },
  {
    key: 'quality',
    nameKey: 'navGroupQuality',
    icon: Scan,
    items: [
      { nameKey: 'traces', path: '/traces', icon: Scan },
      { nameKey: 'evaluation', path: '/evaluation', icon: FlaskConical },
      { nameKey: 'performance', path: '/performance', icon: Timer },
      { nameKey: 'continuityObservability', path: '/continuity-observability', icon: History },
      { nameKey: 'annotation', path: '/annotation/batches', icon: Tags },
      { nameKey: 'classificationAnalytics', path: '/classification-analytics', icon: TrendingUp },
      { nameKey: 'messageFeedback', path: '/message-feedback', icon: MessageSquare },
      { nameKey: 'playground', path: '/playground', icon: Gamepad2 },
    ]
  },
  {
    key: 'costSupply',
    nameKey: 'navGroupCostSupply',
    icon: Boxes,
    items: [
      { nameKey: 'modelCatalog', path: '/model-catalog', icon: Boxes },
      { nameKey: 'costCenter', path: '/cost-center', icon: Wallet },
    ]
  },
  {
    key: 'operations',
    nameKey: 'navGroupOperations',
    icon: PieChart,
    items: [
      ...reportPageItems.map((item) => ({
        nameKey: item.navigationKey,
        path: item.path,
        icon: reportNavIconById[item.id],
      })),
      { nameKey: 'userGrowth', path: '/user-growth', icon: TrendingUp },
      { nameKey: 'acquisitionChannels', path: '/acquisition-channels', icon: PieChart },
    ]
  },
  {
    key: 'user',
    nameKey: 'navGroupUser',
    icon: UserCog,
    items: [
      { nameKey: 'users', path: '/users', icon: Users },
      { nameKey: 'subscriptionSettings', path: '/subscription-settings', icon: CreditCard },
      { nameKey: 'promoCodes', path: '/promo-codes', icon: Ticket },
      { nameKey: 'referralSettings', path: '/referral-settings', icon: Gift },
      { nameKey: 'affiliateManagement', path: '/affiliate-management', icon: Megaphone },
      { nameKey: 'wishes', path: '/wishes', icon: Sparkles },
    ]
  },
  {
    key: 'finance',
    nameKey: 'navGroupFinance',
    icon: DollarSign,
    items: [
      { nameKey: 'companyFinanceDashboard', path: '/company-finance', icon: ClipboardList },
      { nameKey: 'financeDashboard', path: '/finance', icon: TrendingUp },
    ]
  },
  {
    key: 'governance',
    nameKey: 'navGroupGovernance',
    icon: Cog,
    items: [
      { nameKey: 'assetManagement', path: '/assets', icon: KeyRound },
      { nameKey: 'systemSettings', path: '/system-settings', icon: Settings },
      { nameKey: 'featureFlags', path: '/feature-flags', icon: Flag },
      { nameKey: 'audit', path: '/audit', icon: History },
      { nameKey: 'monitoring', path: '/monitoring', icon: Activity },
      { nameKey: 'notificationTemplates', path: '/notification-templates', icon: Bell },
    ]
  },
]

const STORAGE_KEY = 'sidebar_expanded_groups'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export function Sidebar({
  collapsed,
  onToggle,
}: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { messages } = useI18n()
  const t = messages.navigation

  const username = localStorage.getItem('admin_username') || localStorage.getItem('admin_email') || 'Admin'

  // Find which group contains the active path
  const activeGroupKey = useMemo(() => {
    for (const group of navGroups) {
      for (const item of group.items) {
        if (isNavItemActive(location.pathname, item)) return group.key
      }
    }
    return null
  }, [location.pathname])

  // Initialize expanded state from localStorage, default to active group
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        return new Set(JSON.parse(saved))
      }
    } catch {
      // ignore parse errors
    }
    // Default: expand the group containing active item
    return activeGroupKey ? new Set([activeGroupKey]) : new Set(['agent'])
  })

  // Save to localStorage when expanded groups change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...expandedGroups]))
  }, [expandedGroups])

  // Auto-expand the group containing the active route.
  // 依赖只含 activeGroupKey：若把 expandedGroups 放进依赖，手动折叠"激活组"会触发本 effect
  // 立即把它加回 → 激活组永远折叠不回去（dora#1271）。函数式更新读最新 prev，无 stale 闭包。
  useEffect(() => {
    if (!activeGroupKey) return
    setExpandedGroups(prev =>
      prev.has(activeGroupKey) ? prev : new Set([...prev, activeGroupKey])
    )
  }, [activeGroupKey])

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupKey)) {
        next.delete(groupKey)
      } else {
        next.add(groupKey)
      }
      return next
    })
  }

  const handleLogout = async () => {
    // Clear Supabase session so refresh tokens are invalidated
    await supabase.auth.signOut({ scope: 'local' })
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_user_id')
    localStorage.removeItem('admin_email')
    localStorage.removeItem('admin_username')
    sessionStorage.removeItem('admin_token')
    navigate('/login', { replace: true })
  }

  const isItemActive = (item: NavItem) => {
    return isNavItemActive(location.pathname, item)
  }

  return (
    <aside className={clsx(
      'bg-white border-r border-gray-200 flex flex-col h-full transition-all duration-300',
      collapsed ? 'w-16' : 'w-64'
    )}>
      {/* Logo & Toggle */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
        {!collapsed && (
          <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {t.appTitle}
          </h1>
        )}
        <button
          onClick={onToggle}
          className={clsx(
            'p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors',
            collapsed && 'mx-auto'
          )}
          title={collapsed ? t.expandSidebar : t.collapseSidebar}
        >
          {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {navGroups.map((group) => {
          const GroupIcon = group.icon
          const isExpanded = expandedGroups.has(group.key)
          const groupName = t[group.nameKey] || group.key
          const hasActiveItem = group.items.some(isItemActive)

          return (
            <div key={group.key} className="space-y-0.5">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group.key)}
                className={clsx(
                  'w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-left',
                  hasActiveItem ? 'text-blue-700 bg-blue-50/50' : 'text-gray-600 hover:bg-gray-100',
                  collapsed && 'justify-center'
                )}
                title={collapsed ? groupName : undefined}
              >
                <GroupIcon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-sm font-medium">{groupName}</span>
                    <ChevronDown className={clsx(
                      'w-4 h-4 transition-transform',
                      !isExpanded && '-rotate-90'
                    )} />
                  </>
                )}
              </button>

              {/* Group Items */}
              {!collapsed && isExpanded && (
                <div className="ml-3 pl-3 border-l border-gray-200 space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon
                    const itemName = t[item.nameKey]
                    const isActive = isItemActive(item)
                    const isEvaluation = item.path === '/evaluation'

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={clsx(
                          'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm',
                          isActive
                            ? isEvaluation
                              ? 'bg-violet-50 text-violet-700 font-medium'
                              : 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                        )}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span>{itemName}</span>
                      </Link>
                    )
                  })}
                </div>
              )}

              {/* Collapsed mode: show items as tooltips on hover */}
              {collapsed && (
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon
                    const itemName = t[item.nameKey]
                    const isActive = isItemActive(item)
                    const isEvaluation = item.path === '/evaluation'

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={clsx(
                          'flex items-center justify-center px-3 py-2 rounded-lg transition-colors',
                          isActive
                            ? isEvaluation
                              ? 'bg-violet-50 text-violet-700'
                              : 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-100'
                        )}
                        title={itemName}
                      >
                        <Icon className="w-4 h-4" />
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* User & Logout */}
      <div className="p-2 border-t border-gray-200">
        {/* Release Notes link */}
        <a
          href="/releases.html"
          target="_blank"
          rel="noopener noreferrer"
          className={clsx(
            'flex items-center gap-3 px-3 py-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors text-sm',
            collapsed && 'justify-center'
          )}
          title={collapsed ? 'Release Notes' : undefined}
        >
          <FileText className="w-4 h-4 flex-shrink-0" />
          {!collapsed && <span>Release Notes</span>}
        </a>

        {!collapsed && (
          <div className="flex items-center gap-3 px-3 py-2 text-gray-700">
            <User className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm truncate flex-1">{username}</span>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={clsx(
            'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors',
            collapsed && 'justify-center'
          )}
          title={collapsed ? t.logout : undefined}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span>{t.logout}</span>}
        </button>
      </div>
    </aside>
  )
}
